import {ListProps} from '@/molecules/Fragments/Filters/Category/typings';

/**
 * @description filter options for PLP categories
 * @note this is the old filter options, used in the old navigation
 */
const plpFilterCategoryOptionsPrev: Readonly<ListProps> = {
  type: 'radio',
  style: 'text',
  options: [
    {
      label: 'all',
      value: 'all',
    },
    {
      label: 'painting',
      value: 'painting',
      sublist: {
        type: 'radio',
        style: 'button',
        options: [
          {label: 'all', value: 'all'},
          {label: 'acrylic', value: 'painting-acrylic'},
          {label: 'gouache', value: 'painting-gouache'},
          {label: 'oil', value: 'painting-oil'},
          {label: 'watercolour', value: 'painting-watercolour'},
          {label: 'other', value: 'painting-other'},
          {label: 'spray paint', value: 'painting-spray-paint'},
          {label: 'mixed-media', value: 'painting-mixed-media'},
        ],
      },
    },
    {
      label: 'printmaking',
      value: 'printmaking',
      sublist: {
        type: 'radio',
        style: 'button',
        options: [
          {label: 'all', value: 'all'},
          {label: 'monoprint', value: 'printmaking-monoprint'},
          {label: 'screenprints', value: 'printmaking-screenprints'},
          {label: 'lithographs', value: 'printmaking-lithographs'},
          {label: 'etchings / engravings', value: 'printmaking-etchings-engravings'},
          {label: 'linocuts', value: 'printmaking-linocuts'},
          {label: 'woodcuts', value: 'printmaking-woodcuts'},
          {label: 'collagraphs', value: 'printmaking-collagraphs'},
          {label: 'others', value: 'printmaking-other'},
        ],
      },
    },
    {
      label: 'sculpture',
      value: 'sculpture',
      sublist: {
        type: 'radio',
        style: 'button',
        options: [
          {label: 'all', value: 'all'},
          {label: 'stone', value: 'sculpture-stone'},
          {label: 'clay', value: 'sculpture-clay'},
          {label: 'wood', value: 'sculpture-wood'},
          {label: 'mixed media', value: 'sculpture-mixed-media'},
          {label: 'relief', value: 'sculpture-relief'},
          {label: 'bronze', value: 'sculpture-bronze'},
          {label: 'other', value: 'sculpture-other'},
        ],
      },
    },
    {
      label: 'photography',
      value: 'photography',
      sublist: {
        type: 'radio',
        style: 'button',
        options: [
          {label: 'all', value: 'all'},
          {label: 'colour', value: 'photography-colour'},
          {label: 'black and white', value: 'photography-black-and-white'},
          {label: 'manipulated', value: 'photography-manipulated'},
        ],
      },
    },
    {
      label: 'drawing',
      value: 'drawing',
      sublist: {
        type: 'radio',
        style: 'button',
        options: [
          {label: 'all', value: 'all'},
          {label: 'charcoal', value: 'drawing-charcoal'},
          {label: 'ink', value: 'drawing-ink'},
          {label: 'pastel', value: 'drawing-pastel'},
          {label: 'pencil', value: 'drawing-pencil'},
          {label: 'other', value: 'drawing-other'},
        ],
      },
    },
    {
      label: 'digital art',
      value: 'digital-art',
      sublist: {
        type: 'radio',
        style: 'button',
        options: [
          {label: 'all', value: 'all'},
          {label: 'giclée', value: 'digital-art-giclee'},
          {label: 'c-type', value: 'digital-art-c-type'},
          {label: 'other', value: 'digital-art-other'},
        ],
      },
    },
    {
      label: 'collage',
      value: 'collage',
    },
  ],
};

/**
 * @description filter options for new PLP category Prints
 */
const newPrints: ListProps['options'][number] = {
  label: 'prints',
  value: 'prints',
  sublist: {
    type: 'radio',
    style: 'button',
    options: [
      /**
       * BE or FE to handle the `all` and `others` options ?
       * Because the 'digital-art' category is not present in the new navigation,
       * but moved in here, which pathname value shall we use for `others` option ?
       * current: `'/art/product_category-printmaking/printmaking-printmaking-other/'` ?
       * or `'/art/product_category-digital-art/digital_art-digital-art-other/'` ?
       */
      {label: 'all', value: 'all'},
      {label: 'monoprint', value: 'prints-monoprint'},
      {label: 'screenprints', value: 'prints-screenprint'},
      {label: 'lithographs', value: 'prints-lithographs'},
      {label: 'etchings & engravings', value: 'prints-etchings-engravings'},
      {label: 'linocuts', value: 'prints-linocuts'},
      {label: 'woodcuts', value: 'prints-woodcuts'},
      {label: 'collagraphs', value: 'prints-collagraphs'},
      {label: 'c-type', value: 'prints-c-type'},
      {label: 'giclee', value: 'prints-giclee'},
      {label: 'other', value: 'prints-other'},
    ],
  },
};

/**
 * @description filter options for PLP categories
 * @note this is the new filter options, to be used in the new navigation
 * - the `digital-art` category is removed, its properties merged into the `prints` category
 * - the `printmaking` category is replaced by `prints` category
 */
const plpFilterCategoryOptionsNew: Readonly<ListProps> = {
  type: 'radio',
  style: 'text',
  options: plpFilterCategoryOptionsPrev.options
    .filter((option) => option.value !== 'digital-art')
    .map((option) => {
      if (option.label === 'printmaking') return newPrints;
      return option;
    }),
};

/**
 * @description filter options for PLP categories based on the navigation type 'new' or 'old'
 * @param {object} params - The parameters for the function.
 * @param {boolean} params.isNewNavigation - Indicates if the new navigation is used.
 * @return {Readonly<ListProps>} readonly object with filter options for PLP categories filter
 */
export const getPlpFilterCategoryOptions = ({isNewNavigation}: {isNewNavigation: boolean}) => {
  return isNewNavigation ? plpFilterCategoryOptionsNew : plpFilterCategoryOptionsPrev;
};
